import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  nomeNaComanda,
  totalDaComanda,
  type AberturaDeComanda,
  type ComandaAberta,
  type PainelDoBar,
} from '@arena/shared/schemas';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { EscolhaDeAluno } from '../alunos/EscolhaDeAluno.jsx';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { Barras } from '../graficos/Barras.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Modal, CorpoDeModal } from '../ui/Modal.jsx';
import { Avatar, Botao, Campo, Card, Nota, Segmentado, Status, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { horaDoInstante } from '../financeiro/formatos.js';
import { CORES_DO_RANKING, contagemDeItens, horaDaAbertura } from './formatos.js';
import { HistoricoDeComandas } from './HistoricoDeComandas.jsx';
import { ModalDeComanda } from './ModalDeComanda.jsx';

type Aba = 'abertas' | 'historico';

/** Os parâmetros que só o histórico usa: saem da URL quando a aba muda. */
const PARAMETROS_DO_HISTORICO = ['de', 'ate', 'forma', 'canceladas'] as const;

/**
 * `#v-bar` — "Bar e comandas".
 *
 * Três blocos do protótipo: as comandas abertas em cartões, as vendas
 * dos últimos sete dias e os mais vendidos do mês — e, na aba Histórico,
 * as que já fecharam ou foram canceladas.
 *
 * A aba mora na URL (`?aba=historico`, mais os filtros do histórico):
 * o link colado abre o mesmo recorte. Mudar a aba ou o filtro usa `replace`:
 * o voltar do navegador sai da página, não desfaz o filtro.
 *
 * A tela faz POLLING curto nas comandas abertas, pela mesma razão da
 * agenda: dois atendentes no balcão, e o cartão precisa sumir quando o
 * outro fecha a comanda. Trinta segundos é o suficiente para o balcão e
 * barato para o banco.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function PaginaDoBar() {
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();
  const [abrindo, setAbrindo] = useState(false);
  const [comandaAberta, setComandaAberta] = useState<string | null>(null);

  const aba: Aba = params.get('aba') === 'historico' ? 'historico' : 'abertas';

  function mudar(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }

  function trocarDeAba(nova: Aba) {
    mudar((p) => {
      if (nova === 'historico') {
        p.set('aba', 'historico');
        return;
      }
      p.delete('aba');
      for (const campo of PARAMETROS_DO_HISTORICO) p.delete(campo);
    });
  }

  const comandas = useQuery({
    queryKey: ['comandas', 'abertas'],
    queryFn: estoqueApi.comandasAbertas,
    refetchInterval: 30_000,
  });
  const painel = useQuery({
    queryKey: ['comandas', 'painel'],
    queryFn: () => estoqueApi.painel(7),
    enabled: aba === 'abertas',
  });

  const quantas = comandas.data?.length ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Bar e comandas</h1>
          <p>
            {quantas} {quantas === 1 ? 'comanda aberta' : 'comandas abertas'} · consumo vinculado à
            pessoa, com baixa automática no fechamento.
          </p>
        </div>
        <div className="head-actions">
          {painel.data && (
            <Status situacao={painel.data.caixaAberto ? 'ok' : 'neutra'}>
              {painel.data.caixaAberto
                ? `Caixa aberto desde ${horaDoInstante(painel.data.caixaAberto.desde)}`
                : 'Caixa fechado'}
            </Status>
          )}
          {pode('comanda', 'abrir') && (
            <Botao icone="plus" onClick={() => setAbrindo(true)}>
              Abrir comanda
            </Botao>
          )}
        </div>
      </div>

      <Nota>
        <b>Estoque e comanda.</b> Uma comanda aberta por pessoa. O item entra por busca parcial na
        lista, e ao fechar o sistema baixa o estoque e lança a venda no financeiro sem digitação
        dupla. O pagamento pode cair na mensalidade do aluno.
      </Nota>

      <div style={{ marginBottom: 16 }}>
        <Segmentado
          aria-label="Comandas"
          valor={aba}
          onChange={trocarDeAba}
          opcoes={[
            { valor: 'abertas', rotulo: 'Abertas' },
            { valor: 'historico', rotulo: 'Histórico' },
          ]}
        />
      </div>

      {aba === 'historico' ? (
        <HistoricoDeComandas params={params} mudar={mudar} onAbrir={setComandaAberta} />
      ) : (
        <>
          {comandas.isPending ? (
            <EsqueletoDeCard linhas={4} />
          ) : comandas.isError ? (
            <Erro
              detalhe={mensagemDoErro(comandas.error)}
              onTentarDeNovo={() => void comandas.refetch()}
            />
          ) : comandas.data.length === 0 ? (
            <Card>
              <Vazio
                acao={
                  pode('comanda', 'abrir') ? (
                    <Botao pequeno onClick={() => setAbrindo(true)}>
                      Abrir a primeira
                    </Botao>
                  ) : undefined
                }
              >
                Nenhuma comanda aberta agora.
              </Vazio>
            </Card>
          ) : (
            <div className="grid g3">
              {comandas.data.map((comanda) => (
                <CartaoDeComanda
                  key={comanda.id}
                  comanda={comanda}
                  onAbrir={() => setComandaAberta(comanda.id)}
                />
              ))}
            </div>
          )}

          <div className="grid g-2-1">
            <Card titulo="Vendas dos últimos 7 dias" dica="passe o mouse nas barras">
              {painel.isPending ? (
                <EsqueletoDeCard linhas={3} />
              ) : painel.isError ? (
                <Erro
                  detalhe={mensagemDoErro(painel.error)}
                  onTentarDeNovo={() => void painel.refetch()}
                />
              ) : (
                <VendasPorDia painel={painel.data} />
              )}
            </Card>

            <Card
              titulo="Mais vendidos no mês"
              dica={painel.data ? `desde ${dataCurta(painel.data.maisVendidosDesde)}` : undefined}
            >
              {painel.data && painel.data.maisVendidos.length > 0 ? (
                <BarrasHorizontais
                  itens={painel.data.maisVendidos.map((produto, indice) => ({
                    nome: produto.nome,
                    valor: produto.unidades,
                    rotulo: `${produto.unidades} un`,
                    cor: CORES_DO_RANKING[indice % CORES_DO_RANKING.length] as string,
                    dica:
                      produto.margem === null
                        ? `${dinheiro(produto.receita)} no mês.`
                        : `${dinheiro(produto.receita)} no mês.<br><em>margem de ${Math.round(
                            produto.margem / 100,
                          )}%</em>`,
                  }))}
                />
              ) : (
                <Vazio>Nenhuma venda fechada neste mês.</Vazio>
              )}
            </Card>
          </div>
        </>
      )}

      {abrindo && (
        <ModalDeAbertura
          onFechar={() => setAbrindo(false)}
          onVer={(id) => {
            setAbrindo(false);
            setComandaAberta(id);
          }}
        />
      )}
      {comandaAberta && (
        <ModalDeComanda comandaId={comandaAberta} onFechar={() => setComandaAberta(null)} />
      )}
    </>
  );
}

/** O cartão de `#v-bar`: quem, desde quando, o que consumiu e quanto. */
function CartaoDeComanda({ comanda, onAbrir }: { comanda: ComandaAberta; onAbrir: () => void }) {
  const total = totalDaComanda(comanda.itens);
  const nome = nomeNaComanda(comanda);

  return (
    <Card className="clickable">
      <div className="card-head" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar nome={nome} />
          <div>
            <h2>{nome}</h2>
            <span className="hint">aberta às {horaDaAbertura(comanda)}</span>
          </div>
        </div>
        <Status situacao="atencao">Aberta</Status>
      </div>

      {comanda.itens.length === 0 ? (
        <Vazio>Nada lançado ainda.</Vazio>
      ) : (
        <ul className="feed" style={{ marginBottom: 12 }}>
          {comanda.itens.map((item) => (
            <li key={item.id} style={{ padding: '8px 0' }}>
              <span style={{ flex: 1 }}>
                <b>
                  {item.quantidade}× {item.produto.nome}
                </b>
              </span>
              <span className="rt">
                <b className="num">{dinheiro(item.quantidade * item.precoUnitario)}</b>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid var(--line)',
          paddingTop: 12,
        }}
      >
        <span className="sub">{comanda.observacao ?? contagemDeItens(comanda)}</span>
        <b className="num" style={{ fontSize: 17 }}>
          {dinheiro(total)}
        </b>
      </div>

      <Botao variante="fantasma" style={{ width: '100%', marginTop: 12 }} onClick={onAbrir}>
        Abrir comanda
      </Botao>
    </Card>
  );
}

function VendasPorDia({ painel }: { painel: PainelDoBar }) {
  if (painel.porDia.length === 0) {
    return <Vazio>Nenhuma venda fechada nos últimos sete dias.</Vazio>;
  }

  return (
    <Barras
      cores={['var(--quadra-1)']}
      colunas={painel.porDia.map((dia) => ({
        x: dataCurta(dia.data),
        valores: [dia.receita],
        dicas: [`${dinheiro(dia.receita)} · ${dia.unidades} itens`],
      }))}
    />
  );
}

type Para = 'aluno' | 'avulso';

/**
 * Abrir comanda: para um ALUNO ou para um nome avulso.
 *
 * O aluno entra por id porque é ele que liga a comanda à mensalidade; o
 * avulso, por nome, porque gente sem cadastro consome no bar e recusar
 * isso empurraria a venda para fora do sistema.
 *
 * Se o aluno já tem comanda aberta o servidor recusa (409), e a tela
 * oferece abrir a que existe em vez de deixar a pessoa procurando o cartão.
 */
function ModalDeAbertura({
  onFechar,
  onVer,
}: {
  onFechar: () => void;
  onVer: (comandaId: string) => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const [para, setPara] = useState<Para>('aluno');
  const [aluno, setAluno] = useState<{ id: string; nome: string } | null>(null);
  const [nome, setNome] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | undefined>();
  const [jaAberta, setJaAberta] = useState<string | null>(null);

  const pessoa: Pick<AberturaDeComanda, 'alunoId' | 'nomeAvulso'> | null =
    para === 'aluno'
      ? aluno
        ? { alunoId: aluno.id }
        : null
      : nome.trim().length >= 2
        ? { nomeAvulso: nome.trim() }
        : null;

  const limparAviso = () => {
    setErro(undefined);
    setJaAberta(null);
  };

  const abrir = useMutation({
    // Uma nova tentativa tira da tela o erro e o "Ver comanda aberta" da anterior.
    onMutate: limparAviso,
    mutationFn: (quem: NonNullable<typeof pessoa>) =>
      estoqueApi.abrirComanda({
        ...quem,
        ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
      }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['comandas'] });
      avisar('Comanda aberta.');
      onFechar();
    },
    onError: async (e: unknown, quem) => {
      setErro(mensagemDoErro(e));
      if (e instanceof ErroDeApi && e.status === 409 && quem.alunoId) {
        // A lista da tela pode estar atrasada: pergunta de novo, para
        // achar a comanda que o servidor acabou de dizer que existe.
        try {
          const abertas = await cliente.fetchQuery({
            queryKey: ['comandas', 'abertas'],
            queryFn: estoqueApi.comandasAbertas,
            staleTime: 0,
          });
          setJaAberta(abertas.find((c) => c.alunoId === quem.alunoId)?.id ?? null);
        } catch {
          // Sem a lista não há o que oferecer; a mensagem do 409 já está na tela.
        }
      }
    },
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Abrir comanda"
      descricao="Uma comanda aberta por pessoa."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => pessoa && abrir.mutate(pessoa)}
            disabled={!pessoa || abrir.isPending}
          >
            {abrir.isPending ? 'Abrindo…' : 'Abrir comanda'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {erro && <Nota>{erro}</Nota>}
        {jaAberta && (
          <div>
            <Botao variante="contorno" pequeno onClick={() => onVer(jaAberta)}>
              Ver comanda aberta
            </Botao>
          </div>
        )}

        <Campo rotulo="Quem vai consumir">
          <Segmentado
            aria-label="Quem vai consumir"
            valor={para}
            onChange={(valor) => {
              setPara(valor);
              limparAviso();
            }}
            opcoes={[
              { valor: 'aluno', rotulo: 'Aluno' },
              { valor: 'avulso', rotulo: 'Sem cadastro' },
            ]}
          />
        </Campo>

        {para === 'aluno' ? (
          aluno ? (
            <Campo rotulo="Aluno" ajuda="O consumo pode ser lançado na mensalidade dele.">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  background: 'var(--bg)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-sm)',
                }}
              >
                <Avatar nome={aluno.nome} />
                <b style={{ flex: 1 }}>{aluno.nome}</b>
                <Botao
                  variante="fantasma"
                  pequeno
                  onClick={() => {
                    setAluno(null);
                    limparAviso();
                  }}
                >
                  Trocar
                </Botao>
              </div>
            </Campo>
          ) : (
            <EscolhaDeAluno
              onEscolher={(escolhido) => {
                setAluno(escolhido);
                limparAviso();
              }}
            />
          )
        ) : (
          <Campo
            rotulo="Nome"
            ajuda="Quem vai consumir. Sem cadastro, não há mensalidade onde lançar."
          >
            <input
              value={nome}
              onChange={(e) => {
                setNome(e.target.value);
                limparAviso();
              }}
              placeholder="Ex.: Diego"
            />
          </Campo>
        )}

        <Campo rotulo="Observação" ajuda="Opcional. Ex.: locação avulsa · Quadra 1">
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </Campo>
      </CorpoDeModal>
    </Modal>
  );
}
